import axios from "axios";
import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type Query,
  type QueryClient,
} from "@tanstack/react-query";
import type {
  IntakeService,
  IntakeSubmissionActivityService,
  IntakeSubmissionCommentService,
} from "@/services/intake.service";
import type {
  TIntakeDefinition,
  TIntakeSubmission,
  TIntakeSubmissionActivity,
  TIntakeSubmissionComment,
  TIntakeSubmissionsListQuery,
  TIntakeSubmissionsPage,
  TLinkableProjectsPage,
  TWorkflowCurrentStage,
} from "@/types/intake";
import type { TLinkedProject } from "@/types/project";
import type { IPriorityBreakdown } from "@/types/request-prioritization";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The requests reads (NG-2808):
 *
 * - **list** `[slug, uid, 'requests', 'list', params]`: the requests table, one
 *   cursor page per fetch, keyed by every server filter, the sort, the archive
 *   view and the group-by. Also the unfiltered first page the goal detail and
 *   the link-request dialog pick from. Mutable working data.
 * - **board** `[slug, uid, 'requests', 'board', stateId, params]`: one status
 *   column of the board, paged on its own. Mutable.
 * - **detail** `[slug, uid, 'request', id]`: one request with the caller's
 *   verdicts (`can_edit`, `can_change_status`, …). Access-sensitive: reused for
 *   60 s, so a switch between the request's tabs (each its own route) reads it
 *   once, and an owner taken off it loses it through the `access:self` hint
 *   (`flows/request-ownership.spec.ts`, NG-3012).
 * - **definition** `[slug, uid, 'request', id, 'definition']`: the request's
 *   render definition from its pinned snapshot, a fixed copy, so reference
 *   data. Under the detail's key, so dropping the detail drops it.
 * - **stage** `[slug, uid, 'request', id, 'stage']`: the workflow run's current
 *   Page or Review with the caller's verdicts. Access-sensitive, under the detail.
 * - **activity** / **comments** `[slug, uid, 'request', id, 'activity' | 'comments']`:
 *   the request's feeds. Mutable working data, under the detail.
 * - **linked-projects** / **linkable-projects** / **priority**, under the
 *   detail: the projects linked to it, the picker of projects to link (keyed by
 *   search), and the priority breakdown at one version of the request. Mutable.
 * - **reviewer queue** `[slug, uid, 'request-reviews', 'pending']`: the caller's
 *   "awaiting my review" requests. Access-sensitive, and outside `'requests'`
 *   because it is one array, not pages.
 *
 * Lists sit under `'requests'` and one request under `'request'`, so a list
 * invalidation never refetches an open detail. Every read is a plain GET, so all
 * declare `"safe-read"`.
 *
 * @writers The row writers in `IntakeStore` (status, owners, priority, archive,
 * delete, inline answers) patch every cached list page, board column and detail
 * holding the row through `writeRequestRow` below, then refetch whichever list
 * entries were read while the write was in flight (NG-2463). `rereadRequest`
 * writes the detail it read.
 */

/** A list's parameters: everything but the cursor, which the page walk owns. */
export type RequestsListParams = Omit<TIntakeSubmissionsListQuery, "cursor">;

/** The page size of one board column. */
export const BOARD_COLUMN_PAGE_SIZE = 15;

const LISTS = "requests";
const ONE = "request";

/** The list key. */
export function requestsListKey(scope: QueryScope, params: RequestsListParams) {
  return scopedKey(scope, LISTS, "list", params);
}

/** The board column key. */
export function requestsBoardColumnKey(
  scope: QueryScope,
  stateId: string,
  params: RequestsListParams,
) {
  return scopedKey(scope, LISTS, "board", stateId, params);
}

/** The detail key. */
export function requestDetailKey(scope: QueryScope, requestId: string) {
  return scopedKey(scope, ONE, requestId);
}

/** The workflow stage key, under the detail's. */
function requestStageKey(scope: QueryScope, requestId: string) {
  return scopedKey(scope, ONE, requestId, "stage");
}

/** The definition key, under the detail's. */
export function requestDefinitionKey(scope: QueryScope, requestId: string) {
  return scopedKey(scope, ONE, requestId, "definition");
}

function nextCursor(last: TIntakeSubmissionsPage, cursor: string | null): string | undefined {
  return last.next_page_results && last.next_cursor && last.next_cursor !== cursor
    ? last.next_cursor
    : undefined;
}

function pagedRequests(
  service: Pick<IntakeService, "listSubmissions">,
  scope: QueryScope,
  queryKey: readonly unknown[],
  params: TIntakeSubmissionsListQuery,
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey,
    initialPageParam: null as string | null,
    queryFn: scopedRead<TIntakeSubmissionsPage, string | null>(
      (request, { pageParam }) =>
        service.listSubmissions(
          scope.tenant,
          pageParam ? { ...params, cursor: pageParam } : params,
          request,
        ),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TIntakeSubmissionsPage, _all, cursor) => nextCursor(last, cursor),
  });
}

/** The requests table (or a picker's unfiltered list), one cursor page per fetch. */
export function requestsListQuery(
  service: Pick<IntakeService, "listSubmissions">,
  scope: QueryScope,
  params: RequestsListParams,
) {
  return pagedRequests(service, scope, requestsListKey(scope, params), params);
}

/**
 * One board column: the list's filters pinned to one status, at the board's
 * page size. `params` must not carry `status`: the column decides it.
 */
export function requestsBoardColumnQuery(
  service: Pick<IntakeService, "listSubmissions">,
  scope: QueryScope,
  stateId: string,
  params: RequestsListParams,
) {
  return pagedRequests(service, scope, requestsBoardColumnKey(scope, stateId, params), {
    ...params,
    status: [stateId],
    per_page: BOARD_COLUMN_PAGE_SIZE,
  });
}

/** One request, with the caller's verdicts. */
export function requestDetailQuery(
  service: Pick<IntakeService, "getSubmission">,
  scope: QueryScope,
  requestId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: requestDetailKey(scope, requestId),
    queryFn: scopedRead((request) => service.getSubmission(scope.tenant, requestId, request), {
      retry: "safe-read",
    }),
  });
}

/** The request's render definition, from its pinned snapshot. */
export function requestDefinitionQuery(
  service: Pick<IntakeService, "getSubmissionDefinition">,
  scope: QueryScope,
  requestId: string,
  onLoad?: (definition: TIntakeDefinition) => void,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: requestDefinitionKey(scope, requestId),
    queryFn: scopedRead<TIntakeDefinition>(
      async (request) => {
        const { definition } = await service.getSubmissionDefinition(
          scope.tenant,
          requestId,
          request,
        );
        onLoad?.(definition);
        return definition;
      },
      { retry: "safe-read" },
    ),
  });
}

/**
 * The current stage of the request's workflow run (`GET
 * …/workflow/current-page/`): the Page or Review it waits on, with the
 * caller's verdicts (`can_submit`, `can_decide`, …), so access-sensitive:
 * reused for 60 s, so a switch between the request's tabs reads it once
 * (NG-3012). The detail page's review and page panels share it: one read, not
 * one each. `null` for a request with no workflow run, which 404s here: an
 * answer, cached like any other, not an error each tab switch asks again.
 *
 * @writers `IntakeStore.submitWorkflowPage` and `completeWorkflowStep` put the
 * stage they return (`putCachedStage`); a decision or a return invalidates it
 * (`invalidateCachedStage`).
 */
export function requestStageQuery(
  service: Pick<IntakeService, "getWorkflowCurrentStage">,
  scope: QueryScope,
  requestId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: requestStageKey(scope, requestId),
    queryFn: scopedRead<TWorkflowCurrentStage | null>(
      async (request) => {
        try {
          return await service.getWorkflowCurrentStage(scope.tenant, requestId, request);
        } catch (error) {
          // No workflow run: the panels hide, nothing failed.
          if (axios.isAxiosError(error) && error.response?.status === 404) return null;
          throw error;
        }
      },
      { retry: "safe-read" },
    ),
  });
}

/** Put the stage a page submit or a step completion returned. */
export function putCachedStage(
  client: QueryClient,
  scope: QueryScope,
  requestId: string,
  stage: TWorkflowCurrentStage,
): void {
  client.setQueryData(requestStageKey(scope, requestId), stage);
}

/** Re-read the request's stage wherever it is shown, after a decision moved the run. */
export function invalidateCachedStage(
  client: QueryClient,
  tenant: string,
  requestId: string,
): void {
  client
    .invalidateQueries({
      predicate: (query) => isRequestPartQuery(query, tenant, requestId, "stage"),
    })
    .catch((error: unknown) => {
      console.warn("[requests] re-reading a workflow stage failed", { tenant, requestId, error });
    });
}

// ---- Activity and comments ----------------------------------------------

/** The activity feed key, under the detail's. */
function requestActivityKey(scope: QueryScope, requestId: string) {
  return scopedKey(scope, ONE, requestId, "activity");
}

/** The comments key, under the detail's. */
function requestCommentsKey(scope: QueryScope, requestId: string) {
  return scopedKey(scope, ONE, requestId, "comments");
}

/** The activity sheet reads the newest rows only, as the store did. */
const ACTIVITY_LIMIT = 50;

/**
 * The request's activity feed, newest first. Working data, like a project's
 * feed (`project-resources.ts`).
 *
 * @writers A comment posted or deleted marks it stale (`markCachedActivityStale`),
 * since the server logs a row for each.
 */
export function requestActivityQuery(
  service: Pick<IntakeSubmissionActivityService, "list">,
  scope: QueryScope,
  requestId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: requestActivityKey(scope, requestId),
    queryFn: scopedRead<TIntakeSubmissionActivity[]>(
      (request) => service.list(scope.tenant, requestId, ACTIVITY_LIMIT, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * The request's comments, newest first. Working data.
 *
 * @writers `useRequestCommentWriters` puts a posted comment
 * (`putCachedComment`).
 */
export function requestCommentsQuery(
  service: Pick<IntakeSubmissionCommentService, "list">,
  scope: QueryScope,
  requestId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: requestCommentsKey(scope, requestId),
    queryFn: scopedRead<TIntakeSubmissionComment[]>(
      (request) => service.list(scope.tenant, requestId, request),
      { retry: "safe-read" },
    ),
  });
}

/** One request's `part` entry (stage, activity, comments) of `tenant`, under any principal. */
function isRequestPartQuery(
  query: Query,
  tenant: string,
  requestId: string,
  part: string,
): boolean {
  const key = query.queryKey;
  return key[0] === tenant && key[2] === ONE && key[3] === requestId && key[4] === part;
}

function feedQueries(client: QueryClient, tenant: string, requestId: string, feed: string) {
  return client.getQueryCache().findAll({
    predicate: (q) => isRequestPartQuery(q, tenant, requestId, feed),
  });
}

/** A posted comment goes to the top of the cached comments. */
export function putCachedComment(
  client: QueryClient,
  tenant: string,
  requestId: string,
  comment: TIntakeSubmissionComment,
): void {
  for (const query of feedQueries(client, tenant, requestId, "comments")) {
    rewrite<TIntakeSubmissionComment[]>(client, query, (rows) => [
      comment,
      ...rows.filter((c) => c.id !== comment.id),
    ]);
  }
}

/** Re-read the request's activity where it is shown; elsewhere on its next mount. */
export function markCachedActivityStale(
  client: QueryClient,
  tenant: string,
  requestId: string,
): void {
  client
    .invalidateQueries({
      predicate: (q) => isRequestPartQuery(q, tenant, requestId, "activity"),
    })
    .catch((error: unknown) => {
      console.warn("[requests] marking the activity feed stale failed", {
        tenant,
        requestId,
        error,
      });
    });
}

// ---- Linked projects and the priority breakdown ------------------------

/** The page size of the link-projects picker. */
const LINKABLE_PAGE_SIZE = 25;

/**
 * The projects linked to the request. Working data.
 *
 * @writers The linked projects section's unlink drops the row
 * (`dropCachedLinkedProject`); the link dialog marks it and the picker stale
 * (`markCachedLinksStale`).
 */
export function linkedProjectsQuery(
  service: Pick<IntakeService, "listSubmissionLinkedProjects">,
  scope: QueryScope,
  requestId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: scopedKey(scope, ONE, requestId, "linked-projects"),
    queryFn: scopedRead<TLinkedProject[]>(
      (request) => service.listSubmissionLinkedProjects(scope.tenant, requestId, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * The projects the caller may link to the request, one cursor page per fetch,
 * keyed by the search. Working data.
 *
 * @writers The link dialog marks it stale after a link (`markCachedLinksStale`).
 */
export function linkableProjectsQuery(
  service: Pick<IntakeService, "listLinkableProjects">,
  scope: QueryScope,
  requestId: string,
  search: string,
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: scopedKey(scope, ONE, requestId, "linkable-projects", search),
    initialPageParam: null as string | null,
    queryFn: scopedRead<TLinkableProjectsPage, string | null>(
      (request, { pageParam }) =>
        service.listLinkableProjects(
          scope.tenant,
          requestId,
          {
            search: search || undefined,
            cursor: pageParam ?? undefined,
            pageSize: LINKABLE_PAGE_SIZE,
          },
          request,
        ),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TLinkableProjectsPage, _all, cursor) =>
      last.next_page_results && last.next_cursor && last.next_cursor !== cursor
        ? last.next_cursor
        : undefined,
  });
}

/**
 * The request's priority breakdown at one version of the request (its
 * `updated_at`): a requester edit is a new version and a new read. `null` when
 * scoring is off, unconfigured, or overridden. Working data.
 *
 * @writers None: a write to the request moves its version.
 */
export function priorityBreakdownQuery(
  service: Pick<IntakeService, "getSubmissionPriorityBreakdown">,
  scope: QueryScope,
  requestId: string,
  version: string | number | undefined,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: scopedKey(scope, ONE, requestId, "priority", version ?? null),
    queryFn: scopedRead<IPriorityBreakdown | null>(
      async (request) =>
        (await service.getSubmissionPriorityBreakdown(scope.tenant, requestId, request)).breakdown,
      { retry: "safe-read" },
    ),
  });
}

/** An unlinked project leaves the request's cached linked projects. */
export function dropCachedLinkedProject(
  client: QueryClient,
  tenant: string,
  requestId: string,
  projectId: string,
): void {
  for (const query of client.getQueryCache().findAll({
    predicate: (q) => isRequestPartQuery(q, tenant, requestId, "linked-projects"),
  })) {
    rewrite<TLinkedProject[]>(client, query, (rows) => rows.filter((p) => p.id !== projectId));
  }
}

/** Projects were linked: the linked list and the picker read again. */
export function markCachedLinksStale(client: QueryClient, tenant: string, requestId: string): void {
  client
    .invalidateQueries({
      predicate: (q) =>
        isRequestPartQuery(q, tenant, requestId, "linked-projects") ||
        isRequestPartQuery(q, tenant, requestId, "linkable-projects"),
    })
    .catch((error: unknown) => {
      console.warn("[requests] marking the linked projects stale failed", {
        tenant,
        requestId,
        error,
      });
    });
}

// ---- The reviewer queue -------------------------------------------------

/**
 * Its own root, not under `'requests'`: the queue is one unpaged array, and
 * the row writers below rewrite everything under `'requests'` as pages.
 */
const REVIEWS = "request-reviews";

/** The caller's "awaiting my review" queue key. */
function pendingReviewsKey(scope: QueryScope) {
  return scopedKey(scope, REVIEWS, "pending");
}

/**
 * The requests paused on a Review that names the caller (`GET
 * intake/reviews/pending/`). Access-sensitive: who may decide changes with a
 * reviewer reassignment, and a decided request must leave at once.
 *
 * @writers `IntakeStore.submitWorkflowDecision` and `returnWorkflowReview`
 * drop the request from it (`dropCachedPendingReview`); the queue page
 * re-reads it after a decision.
 */
export function pendingReviewsQuery(
  service: Pick<IntakeService, "listPendingReviews">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: pendingReviewsKey(scope),
    queryFn: scopedRead<TIntakeSubmission[]>(
      async (request) => (await service.listPendingReviews(scope.tenant, request)).requests,
      { retry: "safe-read" },
    ),
  });
}

/** Drop a decided or returned request from every cached queue of `tenant`. */
export function dropCachedPendingReview(
  client: QueryClient,
  tenant: string,
  requestId: string,
): void {
  for (const query of client.getQueryCache().findAll({
    predicate: (q) => q.queryKey[0] === tenant && q.queryKey[2] === REVIEWS,
  })) {
    rewrite<TIntakeSubmission[]>(client, query, (rows) => rows.filter((r) => r.id !== requestId));
  }
}

/** The rows of every loaded page, first page first. */
export function requestPageRows(data: InfiniteData<TIntakeSubmissionsPage> | undefined) {
  return data?.pages.flatMap((page) => page.results) ?? [];
}

// ---- Writers ------------------------------------------------------------

type Pages = InfiniteData<TIntakeSubmissionsPage, string | null>;

/** A list or board entry of `tenant`, under any principal. */
function isRequestListQuery(tenant: string, query: Query): boolean {
  const key = query.queryKey;
  return key[0] === tenant && key[2] === LISTS;
}

/** The detail entry of one request of `tenant`, under any principal. */
function isRequestDetailQuery(tenant: string, requestId: string, query: Query): boolean {
  const key = query.queryKey;
  return key[0] === tenant && key[2] === ONE && key[3] === requestId && key.length === 4;
}

function isBoardColumnQuery(query: Query): boolean {
  return query.queryKey[3] === "board";
}

function boardStateOf(query: Query): string | undefined {
  return isBoardColumnQuery(query) ? (query.queryKey[4] as string) : undefined;
}

function mapPages(pages: Pages, map: (page: TIntakeSubmissionsPage) => TIntakeSubmissionsPage) {
  return { ...pages, pages: pages.pages.map(map) };
}

/**
 * Rewrite cached data without changing when it was read: a write's patch is
 * not a read, and `writeRequestRow` tells pages read during the write apart by
 * their `dataUpdatedAt`.
 */
function rewrite<T>(client: QueryClient, query: Query, update: (current: T) => T): void {
  const current = query.state.data as T | undefined;
  if (current === undefined) return;
  client.setQueryData<T>(query.queryKey, update(current), {
    updatedAt: query.state.dataUpdatedAt,
  });
}

/** The cached copy of a request: its detail if loaded, else a list or board row. */
export function findCachedRequest(
  client: QueryClient,
  tenant: string,
  requestId: string,
): TIntakeSubmission | undefined {
  const cache = client.getQueryCache();
  const detail = cache.findAll({
    predicate: (query) => isRequestDetailQuery(tenant, requestId, query),
  });
  for (const query of detail) {
    const data = query.state.data as TIntakeSubmission | undefined;
    if (data) return data;
  }
  for (const query of cache.findAll({ predicate: (q) => isRequestListQuery(tenant, q) })) {
    const pages = query.state.data as Pages | undefined;
    for (const page of pages?.pages ?? []) {
      const row = page.results.find((s) => s.id === requestId);
      if (row) return row;
    }
  }
  return undefined;
}

/**
 * Put `row` in place of the cached request with its id: in its detail, in every
 * list page holding it, and in every board column holding it. With
 * `toStateId`, the board moves the card too: out of every other column and to
 * the top of that one (when loaded), each column's count following.
 */
export function putCachedRequest(
  client: QueryClient,
  tenant: string,
  row: TIntakeSubmission,
  { toStateId }: { toStateId?: string } = {},
): void {
  const cache = client.getQueryCache();
  // A write's row may lack the detail's verdicts, and the detail is reused for
  // 60 s (NG-3012): merge, so the verdicts it was answered with stay.
  for (const query of cache.findAll({
    predicate: (q) => isRequestDetailQuery(tenant, row.id, q),
  })) {
    rewrite<TIntakeSubmission>(client, query, (previous) => ({ ...previous, ...row }));
  }
  for (const query of cache.findAll({ predicate: (q) => isRequestListQuery(tenant, q) })) {
    const state = boardStateOf(query);
    if (toStateId === undefined || state === undefined) {
      rewrite<Pages>(client, query, (pages) =>
        mapPages(pages, (page) => ({
          ...page,
          results: page.results.map((s) => (s.id === row.id ? row : s)),
        })),
      );
      continue;
    }
    rewrite<Pages>(client, query, (pages) => {
      const had = pages.pages.some((page) => page.results.some((s) => s.id === row.id));
      const arriving = state === toStateId;
      if (!had && !arriving) return pages;
      // The count tracks the server's: -1 when the card leaves, +1 when it arrives.
      const delta = arriving ? (had ? 0 : 1) : -1;
      return {
        ...pages,
        pages: pages.pages.map((page, index) => {
          const without = page.results.filter((s) => s.id !== row.id);
          return {
            ...page,
            results: arriving && index === 0 ? [row, ...without] : without,
            total_count: Math.max(0, page.total_count + delta),
          };
        }),
      };
    });
  }
}

/** Mark every cached list page and board column of `tenant` stale: requests
 *  were added outside a row write (a bulk import). Shown lists re-read now. */
export function markCachedRequestListsStale(client: QueryClient, tenant: string): void {
  client
    .invalidateQueries({ predicate: (q) => isRequestListQuery(tenant, q) })
    .catch((error: unknown) => {
      console.warn("[requests] marking the lists stale failed", { tenant, error });
    });
}

/**
 * Merge `patch` into every cached copy of one request (its detail, its
 * last-seen copy, and each list page and board column holding it), leaving
 * every other field of each copy as that copy had it. For a write that answers
 * with a part of the request, not the whole row: a re-read of its pending
 * approvals (NG-2809).
 */
export function patchCachedRequest(
  client: QueryClient,
  tenant: string,
  requestId: string,
  patch: Partial<TIntakeSubmission>,
): void {
  const cache = client.getQueryCache();
  for (const query of cache.findAll({
    predicate: (q) =>
      q.queryKey[0] === tenant &&
      q.queryKey[2] === ONE &&
      q.queryKey[3] === requestId &&
      (q.queryKey.length === 4 || (q.queryKey.length === 5 && q.queryKey[4] === "last-seen")),
  })) {
    rewrite<TIntakeSubmission>(client, query, (current) => ({ ...current, ...patch }));
  }
  for (const query of cache.findAll({ predicate: (q) => isRequestListQuery(tenant, q) })) {
    rewrite<Pages>(client, query, (pages) => {
      if (!pages.pages.some((page) => page.results.some((s) => s.id === requestId))) return pages;
      return mapPages(pages, (page) => ({
        ...page,
        results: page.results.map((s) => (s.id === requestId ? { ...s, ...patch } : s)),
      }));
    });
  }
}

/** Drop a deleted request from every cached list page and board column, and its detail. */
export function removeCachedRequest(client: QueryClient, tenant: string, requestId: string): void {
  const cache = client.getQueryCache();
  for (const query of cache.findAll({ predicate: (q) => isRequestListQuery(tenant, q) })) {
    rewrite<Pages>(client, query, (pages) => {
      if (!pages.pages.some((page) => page.results.some((s) => s.id === requestId))) return pages;
      return mapPages(pages, (page) => ({
        ...page,
        results: page.results.filter((s) => s.id !== requestId),
        total_count: Math.max(0, page.total_count - 1),
      }));
    });
  }
  client.removeQueries({
    predicate: (query) =>
      query.queryKey[0] === tenant && query.queryKey[2] === ONE && query.queryKey[3] === requestId,
  });
}

/** Every cached list page, board column and detail of `tenant`, to put back on a failed write. */
interface RequestCacheSnapshot {
  readonly entries: ReadonlyArray<readonly [readonly unknown[], unknown]>;
}

function snapshotRequestCache(
  client: QueryClient,
  tenant: string,
  requestId: string,
): RequestCacheSnapshot {
  const entries = client
    .getQueryCache()
    .findAll({
      predicate: (q) => isRequestListQuery(tenant, q) || isRequestDetailQuery(tenant, requestId, q),
    })
    .filter((query) => query.state.data !== undefined)
    .map((query) => [query.queryKey, query.state.data] as const);
  return { entries };
}

/**
 * Put back the row `requestId` had before a failed write, in every entry the
 * snapshot held it in, leaving every other row as it is now: a failed write
 * undoes only itself (NG-2463). A board column is put back whole, since the
 * write may have moved the card between columns.
 */
function restoreRequestRow(
  client: QueryClient,
  snapshot: RequestCacheSnapshot,
  requestId: string,
): void {
  const cache = client.getQueryCache();
  for (const [key, before] of snapshot.entries) {
    const query = cache.find({ queryKey: key, exact: true });
    if (!query) continue;
    if (key[2] === ONE) {
      rewrite<TIntakeSubmission>(client, query, () => before as TIntakeSubmission);
      continue;
    }
    if (isBoardColumnQuery(query)) {
      rewrite<Pages>(client, query, () => before as Pages);
      continue;
    }
    const old = (before as Pages).pages
      .flatMap((page) => page.results)
      .find((s) => s.id === requestId);
    if (!old) continue;
    rewrite<Pages>(client, query, (pages) =>
      mapPages(pages, (page) => ({
        ...page,
        results: page.results.map((s) => (s.id === requestId ? old : s)),
      })),
    );
  }
}

/**
 * Run a write to one request against the cache (NG-2463, the Query side of
 * `IntakeStore`'s write clock):
 *
 * 1. Cancel every list read in flight: its server may read the row before the
 *    write commits, and its page would put the old row back.
 * 2. `optimistic`, if given, patches the cache at once.
 * 3. On success the caller patches the server's row in; on failure the row is
 *    put back in every entry, and the error rethrown.
 * 4. Then every list read still in flight is cancelled and made again: it
 *    started during the write, so its server may have read the row before the
 *    write committed, and landing after the patch it would put the old row back
 *    (NG-3246). Every list entry read while the write was in flight, or left
 *    with no data by the cancel, is refetched; the rest are marked stale, since
 *    a row that changed may no longer belong to a filtered list or a group
 *    count.
 *
 * With no client (Query never started) it just runs the write.
 */
export async function writeRequestRow<T>(
  client: QueryClient | null,
  tenant: string,
  requestId: string,
  write: () => Promise<T>,
  {
    optimistic,
    rollback,
  }: {
    optimistic?: () => void;
    /** Undo the optimistic patch on failure, in place of putting the whole row back. */
    rollback?: () => void;
  } = {},
): Promise<T> {
  if (!client) return write();
  const isList = (query: Query) => isRequestListQuery(tenant, query);
  const startedAt = Date.now();
  await client.cancelQueries({ predicate: isList });
  const snapshot = snapshotRequestCache(client, tenant, requestId);
  try {
    optimistic?.();
    return await write();
  } catch (error) {
    if (rollback) rollback();
    else restoreRequestRow(client, snapshot, requestId);
    throw error;
  } finally {
    const inFlight = new Set(
      client
        .getQueryCache()
        .findAll({ predicate: (q) => isList(q) && q.state.fetchStatus === "fetching" }),
    );
    if (inFlight.size > 0) {
      // Cancelled first: a read with no data yet is not cancelled by a refetch,
      // which would hand back the read in flight instead of making a new one.
      const wasInFlight = (q: Query) => inFlight.has(q);
      client
        .cancelQueries({ predicate: wasInFlight })
        .then(() => client.invalidateQueries({ predicate: wasInFlight, refetchType: "all" }))
        .catch((error: unknown) => {
          console.warn("[requests] re-reading lists in flight after a write failed", {
            tenant,
            error,
          });
        });
    }
    const readDuringWrite = (query: Query) =>
      isList(query) &&
      !inFlight.has(query) &&
      (query.state.data === undefined || query.state.dataUpdatedAt >= startedAt);
    client.invalidateQueries({ predicate: readDuringWrite }).catch((error: unknown) => {
      console.warn("[requests] re-reading lists after a write failed", { tenant, error });
    });
    client
      .invalidateQueries({
        predicate: (q) => isList(q) && !inFlight.has(q) && !readDuringWrite(q),
        refetchType: "none",
      })
      .catch((error: unknown) => {
        console.warn("[requests] marking lists stale after a write failed", { tenant, error });
      });
  }
}
